/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useEffect, useRef, useState } from "react";
import { ExternalLink, FileDown, Link, Mail } from "lucide-react";

interface ResultActionsProps {
  toolSlug: string;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  shortId?: string;
}

export function ResultActions({
  toolSlug,
  inputs,
  outputs,
  shortId: initialShortId,
}: ResultActionsProps) {
  const [shortId, setShortId] = useState<string | undefined>(initialShortId);
  const [saved, setSaved] = useState(!!initialShortId);
  const [copied, setCopied] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const saveAttempted = useRef(false);

  // Auto-save on first render if no shortId provided
  useEffect(() => {
    if (initialShortId || saveAttempted.current) return;
    saveAttempted.current = true;

    async function save() {
      try {
        const res = await fetch("/api/public/tool-results", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ toolSlug, inputs, outputs }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setShortId(data.shortId);
        setSaved(true);
      } catch {
        // Silently fail — actions will be disabled without shortId
      }
    }

    save();
  }, [initialShortId, toolSlug, inputs, outputs]);

  async function handleCopyLink() {
    if (!shortId) return;
    const url = `${window.location.origin}/r/${shortId}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!shortId || !email) return;
    setEmailSending(true);
    try {
      const res = await fetch(`/api/public/tool-results/${shortId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setEmailSent(true);
      }
    } catch {
      // Fail silently
    } finally {
      setEmailSending(false);
    }
  }

  async function handleDownloadPdf() {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const { generateToolResultPdf } = await import(
        "@/lib/tools/tool-result-pdf"
      );
      const { getToolBySlug } = await import("@/lib/tools/tool-registry");
      const tool = getToolBySlug(toolSlug);
      const displayConfig = tool?.displayConfig ?? {
        inputLabels: {},
        outputLabels: {},
      };
      const blob = await generateToolResultPdf(
        tool?.title ?? toolSlug,
        inputs,
        outputs,
        displayConfig
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${toolSlug}-result.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // PDF generation failed
    } finally {
      setPdfGenerating(false);
    }
  }

  const buttonClasses =
    "flex items-center gap-2 rounded-lg bg-background px-4 py-2.5 text-sm font-medium text-foreground border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-muted/50 p-4">
        {saved && (
          <span className="mr-auto text-xs text-muted-foreground">
            Result saved
          </span>
        )}

        <button
          type="button"
          className={buttonClasses}
          onClick={handleCopyLink}
          disabled={!shortId}
        >
          <Link className="h-4 w-4" />
          {copied ? "Copied!" : "Share Link"}
        </button>

        <button
          type="button"
          className={buttonClasses}
          onClick={() => setShowEmail((prev) => !prev)}
          disabled={!shortId}
        >
          <Mail className="h-4 w-4" />
          Email Me
        </button>

        <button
          type="button"
          className={buttonClasses}
          onClick={handleDownloadPdf}
          disabled={pdfGenerating}
        >
          <FileDown className="h-4 w-4" />
          {pdfGenerating ? "Generating..." : "Save as PDF"}
        </button>

        <a
          href={`/auth/sign-up?redirect=/app/estimates/new?from_tool=${toolSlug}&result=${shortId ?? ""}`}
          className={buttonClasses}
        >
          <ExternalLink className="h-4 w-4" />
          Open in Contractor Co-Pilot
        </a>
      </div>

      {showEmail && !emailSent && (
        <form
          onSubmit={handleEmailSubmit}
          className="flex items-center gap-2 rounded-xl border bg-muted/50 p-4"
        >
          <input
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={emailSending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {emailSending ? "Sending..." : "Send"}
          </button>
        </form>
      )}

      {emailSent && showEmail && (
        <div className="rounded-xl border bg-muted/50 p-4 text-sm text-muted-foreground">
          Results sent to {email}
        </div>
      )}
    </div>
  );
}
